import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { SmediumLoader } from 'src/common/loaders/SmediumLoader';
import { useAnalyticsContext } from 'src/common/metrics';
import { Link } from 'src/common/styled';
import { redirectToAmazonApp } from 'src/utils/redirectUtils';
/**
 * Interstitial shown while the host SMA is asked to open Amazon.
 *
 * `sendOpenLinkMessage` posts `OPEN_EXTERNAL_LINK` and returns rather than navigating, so this
 * component stays mounted for as long as the host takes to react — and indefinitely if it never
 * does. Rendering nothing here would leave the customer on a blank page with no way out, which
 * is exactly the case `DUPLICATE_OPEN_EXTERNAL_LINK` alarms on.
 *
 * Copy and link treatment are carried over from Painter 1.0's redirect interstitial: strings
 * `amazonRedirect` and `goNow` from `HandshakePainterServiceCdk/src/i18n/Strings.puff.json`, and
 * its `.redirectlink` rule in `error_redirect.css`, which underlines on hover only. That is why
 * this link is not passed `underline`, unlike the ones in `InvalidAuthPage`.
 */
export const RedirectingToAmazonPage = ({ reason }) => {
    const [bundle, isBundleLoading] = useBundle('pages.redirectingToAmazon');
    const [retryCount, setRetryCount] = useState(0);
    const rumClient = useAnalyticsContext();
    const handleRetry = () => {
        const attempt = retryCount + 1;
        const preferDirectNavigation = retryCount >= 1;
        // A tap means the customer is still sitting here, so the handoff has not moved them. This is
        // the health signal for the off ramp: compare its rate against redirect_to_amazon_app to see
        // how often hosts are silently dropping OPEN_EXTERNAL_LINK. attempt separates a first tap
        // (message resent) from a second (gave up and navigated).
        rumClient.recordEvent('REDIRECT_TO_AMAZON_GO_NOW_CLICKED', { reason, attempt, preferDirectNavigation });
        // The first tap goes through the SMA again, in case the message sent on mount was dropped.
        // A second tap means the customer is still here after two asks, so stop asking and navigate.
        redirectToAmazonApp(reason, { preferDirectNavigation });
        setRetryCount(attempt);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            // Space would otherwise scroll the page
            event.preventDefault();
            handleRetry();
        }
    };
    // No isBundleLoading early return: the failure that brings the customer here is often the same
    // network failure that breaks this bundle's fetch, and gating the only escape hatch on that
    // fetch would recreate the dead end this page exists to remove.
    return (_jsxs(Column, { spacingInset: "400", spacing: "450", minHeight: "100vh", alignmentHorizontal: "center", alignmentVertical: "center", "data-testid": "redirecting-to-amazon", children: [_jsx(SmediumLoader, {}), _jsxs(Column, { spacing: "300", children: [_jsx(Text, { alignment: "center", "data-testid": "redirecting-message", children: isBundleLoading ? '' : bundle.getMessage('amazonRedirect') }), _jsx(Text, { alignment: "center", children: _jsx(Link, { role: "button", tabIndex: 0, onClick: handleRetry, onKeyDown: handleKeyDown, "data-testid": "retry-link", children: isBundleLoading ? 'Go now' : bundle.getMessage('goNow') }) })] })] }));
};
